import React, { useState } from 'react'
import { Box, Typography } from 'transfix-ui/core'
import { AlertFilledIcon, EditIcon } from 'transfix-icons'
import {
  Button,
  INLINE_MESSAGE_VARIANTS,
  IconButton,
  InlineMessage,
  TAG_STYLE,
  brandColors,
} from 'transfix-ui'
import formatCurrency from 'utils/formatCurrency'
import { ValueField } from '../../../ValueField'
import { TRANSFIX_FLAGS_POLICY_TYPES } from '../constants'
import { IInsurancePolicyFragment } from '../graphql/InsurancePoliciesFragment'
import { getIsCoverageSufficent, getIsExpired } from '../utils'
import EffectiveDateTag from './EffectiveDateTag'
import { TransfixFlagEditModal } from './TransfixFlagEditModal/TransfixFlagEditModal'
import { TransfixFlagsListedAs } from './TransfixFlagsListedAs'

interface IInsuranceTableRow {
  insurancePolicy: IInsurancePolicyFragment
  refetch: () => void
  index: number
}

const InsuranceTabRow = ({ index, insurancePolicy, refetch }: IInsuranceTableRow) => {
  const {
    anyAuto,
    coverageLimit,
    effectiveDate,
    expirationDate,
    id: highwayInsurancePolicyId,
    insuranceCertificate,
    insurer,
    isRefrigerationBreakdownIncluded,
    isType,
    policyNumber,
    scheduledAutos,
    transfixFlags,
  } = insurancePolicy || {}
  const { name } = insurer || {}
  const { signedUrl } = insuranceCertificate || {}
  const {
    transfixAdditionalInsured,
    transfixAdditionalInsuredUpdatedAt,
    transfixAdditionalInsuredUpdatedBy,
    transfixHolder,
    transfixHolderUpdatedAt,
    transfixHolderUpdatedBy,
    transfixLossPayee,
    transfixLossPayeeUpdatedAt,
    transfixLossPayeeUpdatedBy,
  } = transfixFlags || {}

  const documentsValue = signedUrl ? (
    <Box ml='-10px'>
      <Button href={signedUrl} target='_blank' variant='text'>
        Certificate of Insurance
      </Button>
    </Box>
  ) : (
    'No Data'
  )
  const isExpired = getIsExpired(insurancePolicy)

  const Title = () => {
    const style = isExpired ? TAG_STYLE.error : TAG_STYLE.success
    return (
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Typography variant='subtitle1'>{name}</Typography>
        <EffectiveDateTag
          tagStyle={style}
          effectiveDate={effectiveDate}
          expirationDate={expirationDate}
        />
      </Box>
    )
  }

  const TransfixFlags = () => {
    const [isTransfixFlagEditModalOpen, setIsTransfixFlagEditModalOpen] = useState<boolean>(false)

    const openHandler = () => {
      setIsTransfixFlagEditModalOpen(true)
    }

    const closeHandler = () => {
      refetch()
      setIsTransfixFlagEditModalOpen(false)
    }

    if (!isType) {
      return null
    }

    const {
      TRANSFIX_AS_HOLDER,
      TRANSFIX_LISTED_AS_ADDITIONAL_INSURED,
      TRANSFIX_LISTED_AS_PAYEE,
    } = TRANSFIX_FLAGS_POLICY_TYPES

    const showAsAdditionalInsured = TRANSFIX_LISTED_AS_ADDITIONAL_INSURED.includes(isType)
    const showAsHolder = TRANSFIX_AS_HOLDER.includes(isType)
    const showAsLossPayee = TRANSFIX_LISTED_AS_PAYEE.includes(isType)

    const hideTransfixFlags =
      (!showAsAdditionalInsured && !showAsHolder && !showAsLossPayee) || !highwayInsurancePolicyId

    if (hideTransfixFlags) {
      return null
    }

    return (
      <Box
        display='grid'
        gridTemplateColumns='1fr 1fr 1fr'
        gap='12px'
        position='relative'
        borderTop={`1px solid ${brandColors.coolGray2}`}
        mt={2}
        pt={2}>
        {showAsHolder && (
          <TransfixFlagsListedAs
            title='Holder'
            value={transfixHolder}
            updatedAt={transfixHolderUpdatedAt}
            updatedBy={transfixHolderUpdatedBy}
          />
        )}
        {showAsAdditionalInsured && (
          <TransfixFlagsListedAs
            title='Additional Insured'
            value={transfixAdditionalInsured}
            updatedAt={transfixAdditionalInsuredUpdatedAt}
            updatedBy={transfixAdditionalInsuredUpdatedBy}
          />
        )}
        {showAsLossPayee && (
          <TransfixFlagsListedAs
            title='Loss Payee'
            value={transfixLossPayee}
            updatedAt={transfixLossPayeeUpdatedAt}
            updatedBy={transfixLossPayeeUpdatedBy}
          />
        )}

        <Box position='absolute' right='0' top='8px'>
          <IconButton Icon={EditIcon} color='tertiary' onClick={openHandler} size='large' />
          <TransfixFlagEditModal
            highwayInsurancePolicyId={highwayInsurancePolicyId}
            isType={isType}
            size={6}
            open={isTransfixFlagEditModalOpen}
            onClose={closeHandler}
            transfixAdditionalInsured={transfixAdditionalInsured || null}
            transfixLossPayee={transfixLossPayee || null}
            transfixHolder={transfixHolder || null}
          />
        </Box>
      </Box>
    )
  }

  const isCoverageSufficent = getIsCoverageSufficent(insurancePolicy)

  const InsufficentCoverageAlert = () => {
    if (isCoverageSufficent) {
      return null
    }
    return (
      <Box mb={2}>
        <InlineMessage
          message='Insufficient coverage'
          type={INLINE_MESSAGE_VARIANTS.ALERT}
          IconComponent={() => <AlertFilledIcon color='error' />}
        />
      </Box>
    )
  }

  const isCargoInsurance = isType === 'motor_truck_cargo'
  const isAutoPolicy = isType === 'automobile_liability'
  const autPolicyValue = (anyAuto && 'Any Auto') || (scheduledAutos && 'Scheduled Auto') || null

  return (
    <Box borderBottom={index ? `1px solid ${brandColors.coolGray3}` : ''} mb={2} pb={2}>
      <InsufficentCoverageAlert />
      <Title />
      <Box display='grid' gridTemplateColumns='1fr 1fr 1fr' gap='12px'>
        <ValueField title='Policy Number' value={policyNumber} />
        <ValueField
          title='Limit'
          value={formatCurrency(coverageLimit)}
          color={isExpired ? brandColors.error : null}
        />
        <ValueField title='Documents' value={documentsValue} />
        {isAutoPolicy && <ValueField title='Auto Policy' value={autPolicyValue} />}
        {isCargoInsurance && (
          <ValueField title='Reefer Breakdown' value={isRefrigerationBreakdownIncluded} />
        )}
      </Box>
      <TransfixFlags />
    </Box>
  )
}
export default InsuranceTabRow
